import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoginService } from './login/login.service';
import { SnackbarService } from './snackbar.service';
import { Router } from '@angular/router';

/**
 * HTTP Interceptor that handles 401 Unauthorized responses.
 *
 * When a request returns 401 (token expired, invalid, etc.):
 * 1. Log out the current user
 * 2. Clear stored tokens
 * 3. Show snackbar message
 * 4. Redirect to login page
 *
 * This prevents requests from being silently ignored and ensures users
 * are informed when their session expires.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private auth: LoginService,
    private snackbar: SnackbarService,
    private router: Router,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Handle 401 Unauthorized — token expired, invalid, or missing
        if (error.status === 401) {
          const message = error.error?.message || 'Session expired. Please log in again.';
          console.warn(`[AuthInterceptor] 401 Unauthorized: ${message}`);

          // Clear current user and token
          this.auth.logout();

          // Show message to user
          this.snackbar.error(message);

          // Redirect to login
          this.router.navigate(['/login']);
        } else if (error.status === 403) {
          // Handle 403 Forbidden — user exists but lacks permission
          const message = error.error?.message || 'You do not have permission to access this resource.';
          console.warn(`[AuthInterceptor] 403 Forbidden: ${message}`);
          this.snackbar.error(message);
        } else if (error.status === 0) {
          // Network error (CORS, no connection, etc.)
          console.error('[AuthInterceptor] Network error or CORS issue:', error);
          this.snackbar.error('Network error. Please check your connection.');
        }

        return throwError(() => error);
      }),
    );
  }
}
